import { Box } from '@mui/material';
import { ReactNode } from 'react';
import { useSidebarState } from 'react-admin';

/**
 * Os mesmos defaults que a `<Sidebar>` do react-admin usa no `lodashGet`.
 *
 * O tipo do tema MENTE: declara `sidebar.closedWidth` como `number`
 * obrigatório, mas o nosso tema só define `width`. Ler direto devolve
 * `undefined`, a largura volta a ser `auto` e o fantasma volta com ela.
 */
const DRAWER_WIDTH = 240;
const CLOSED_DRAWER_WIDTH = 55;

/**
 * Raiz do menu — existe só para devolver a largura que o react-admin perdeu.
 *
 * `<Layout menu={...}>` SUBSTITUI o `<RaMenu>`, e com ele foi embora a única
 * coisa que fixava a largura da lista. Sem isso, o container da gaveta
 * (`RaSidebar-fixed`, `position: fixed` e sem largura) encolhe para o conteúdo:
 * a lista fica com a largura do rótulo mais longo e só PARECE estreita porque a
 * gaveta corta o excesso.
 *
 * Esse fantasma desloca tudo que se ancora num item do menu — o painel
 * flutuante do submenu e o popover do `FeatureIntro` nasciam a ~200px da
 * esquerda, soltos no meio da tela, com a barra recolhida em 55px.
 *
 * Os rótulos continuam transbordando a caixa como antes (o `noWrap` do
 * `MenuItemLink` não encolhe abaixo do min-content num flex); o que muda é que
 * agora quem corta é a gaveta, e o retângulo do item passa a ser o visível.
 */
const MenuRoot = ({ children }: { children: ReactNode }) => {
    const [sidebarIsOpen] = useSidebarState();

    return (
        <Box
            sx={theme => ({
                width: sidebarIsOpen
                    ? theme.sidebar?.width ?? DRAWER_WIDTH
                    : theme.sidebar?.closedWidth ?? CLOSED_DRAWER_WIDTH,
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
            })}
        >
            {children}
        </Box>
    );
};

export default MenuRoot;
